import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MilestoneTimeline = ({ milestones, onCelebrate, onShare }) => {
  const [selectedMilestone, setSelectedMilestone] = useState(null);

  const getMilestoneIcon = (type) => {
    switch (type) {
      case 'goal-completed':
        return 'Target';
      case 'streak':
        return 'Flame';
      case 'achievement':
        return 'Award';
      case 'level-up':
        return 'TrendingUp';
      case 'community':
        return 'Users';
      case 'wellness':
        return 'Heart';
      default:
        return 'Star';
    }
  };

  const getMilestoneColor = (type) => {
    switch (type) {
      case 'goal-completed':
        return 'from-success to-success/80';
      case 'streak':
        return 'from-accent to-accent/80';
      case 'achievement':
        return 'from-warning to-warning/80';
      case 'level-up':
        return 'from-primary to-primary/80';
      case 'community':
        return 'from-secondary to-secondary/80';
      case 'wellness':
        return 'from-error to-error/80';
      default:
        return 'from-muted to-muted/80';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const sortedMilestones = [...milestones]?.sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <div className="bg-card rounded-lg border border-border p-6 shadow-wellness">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-heading font-semibold text-foreground">Milestone Timeline</h3>
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            iconName="Filter"
            iconPosition="left"
          >
            Filter
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            iconPosition="left"
          >
            Export
          </Button>
        </div>
      </div>
      <div className="relative">
        {/* Timeline Line */}
        <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-border"></div>
        
        <div className="space-y-6">
          {sortedMilestones?.map((milestone, index) => (
            <div key={milestone?.id} className="relative flex items-start space-x-4">
              {/* Timeline Node */}
              <div className={`relative z-10 w-16 h-16 rounded-full bg-gradient-to-br ${getMilestoneColor(milestone?.type)} 
                flex items-center justify-center shadow-wellness-md transition-all duration-300 hover:scale-110 cursor-pointer
                ${milestone?.isNew ? 'animate-breathing' : ''}`}
                onClick={() => setSelectedMilestone(milestone)}
              >
                <Icon 
                  name={getMilestoneIcon(milestone?.type)} 
                  size={24} 
                  color="white" 
                />
                
                {milestone?.isNew && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-error rounded-full flex items-center justify-center">
                    <span className="text-xs text-white font-bold">!</span>
                  </div>
                )}
              </div>

              {/* Milestone Content */}
              <div className="flex-1 min-w-0 pb-6">
                <div className="bg-muted/30 rounded-lg p-4 hover:bg-muted/50 transition-wellness cursor-pointer"
                     onClick={() => setSelectedMilestone(milestone)}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-base font-heading font-semibold text-foreground">{milestone?.title}</h4>
                    <span className="text-sm text-muted-foreground">{formatDate(milestone?.date)}</span>
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-3">{milestone?.description}</p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      {milestone?.points && (
                        <div className="flex items-center space-x-1 bg-accent/10 px-2 py-1 rounded-full">
                          <Icon name="Star" size={14} className="text-accent" />
                          <span className="text-sm font-medium text-accent-foreground">{milestone?.points} pts</span>
                        </div>
                      )}
                      
                      {milestone?.category && (
                        <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full capitalize">
                          {milestone?.category}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {!milestone?.celebrated && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e?.stopPropagation();
                            onCelebrate(milestone);
                          }}
                          iconName="PartyPopper"
                          iconPosition="left"
                        >
                          Celebrate
                        </Button>
                      )}
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e?.stopPropagation();
                          onShare(milestone);
                        }}
                        iconName="Share2"
                        iconPosition="left"
                      >
                        Share
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Load More */}
      {milestones?.length > 10 && (
        <div className="mt-6 text-center">
          <Button
            variant="outline"
            iconName="ChevronDown"
            iconPosition="left"
          >
            Load More Milestones
          </Button>
        </div>
      )}
      {/* Milestone Detail Modal */}
      {selectedMilestone && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg border border-border p-6 w-full max-w-lg shadow-wellness-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-heading font-semibold text-foreground">Milestone Details</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSelectedMilestone(null)}
              >
                <Icon name="X" size={20} />
              </Button>
            </div>
            
            <div className="text-center mb-6">
              <div className={`w-20 h-20 mx-auto rounded-full bg-gradient-to-br ${getMilestoneColor(selectedMilestone?.type)} 
                flex items-center justify-center shadow-wellness-lg mb-4`}>
                <Icon 
                  name={getMilestoneIcon(selectedMilestone?.type)} 
                  size={32} 
                  color="white" 
                />
              </div>
              
              <h4 className="text-xl font-heading font-bold text-foreground mb-2">
                {selectedMilestone?.title}
              </h4>
              
              <p className="text-muted-foreground mb-4">{selectedMilestone?.description}</p>
              
              <div className="flex items-center justify-center space-x-4 text-sm text-muted-foreground">
                <div className="flex items-center space-x-1">
                  <Icon name="Calendar" size={16} />
                  <span>{formatDate(selectedMilestone?.date)}</span>
                </div>
                
                {selectedMilestone?.points && (
                  <div className="flex items-center space-x-1">
                    <Icon name="Star" size={16} className="text-accent" />
                    <span className="text-accent-foreground">{selectedMilestone?.points} points</span>
                  </div>
                )}
              </div>
            </div>
            
            {selectedMilestone?.details && (
              <div className="bg-muted/30 rounded-lg p-4 mb-6">
                <h5 className="font-medium text-foreground mb-2">Achievement Details</h5>
                <p className="text-sm text-muted-foreground">{selectedMilestone?.details}</p>
              </div>
            )}
            
            <div className="flex items-center space-x-3">
              {!selectedMilestone?.celebrated && (
                <Button
                  variant="default"
                  fullWidth
                  onClick={() => {
                    onCelebrate(selectedMilestone);
                    setSelectedMilestone(null);
                  }}
                  iconName="PartyPopper"
                  iconPosition="left"
                >
                  Celebrate Achievement
                </Button>
              )}
              
              <Button
                variant="outline"
                fullWidth
                onClick={() => {
                  onShare(selectedMilestone);
                  setSelectedMilestone(null);
                }}
                iconName="Share2"
                iconPosition="left"
              >
                Share with Community
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MilestoneTimeline;